import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { createNode } from 'constants/nodes';
import text from 'constants/text';
import { setNodeHover } from 'actions/viewStates';
import TrophicNode from 'components/TrophicNode';
import TrophicNodePlay from 'components/TrophicNodePlay';
import Tooltip from 'components/Tooltip';
import ButtonNew from './inputs/ButtonNew';
import { isNodeFixed } from 'constants/fcm';
import { addNode } from 'actions/matrices';
import { useEditableExperiment } from 'hooks/experiments';
import './TrophicLevel.scss';
import Icon from './Icon';
import icons from 'img/icons';
import classNames from 'classnames';

const trophicCascadeLevels = [
  'Primary',
  'Secondary',
  'Tertiary',
  'Equilibrium',
];

const TrophicLevel = ({
  matrix,
  viewMode,
  currentNodes,
  nodeOverrides,
  nodesToOverride,
  setNodesToOverride,
  trophicCascadeLevel,
  icon,
  level,
  color,
  pseudoLevel,
  onDelete,
  canDelete,
  draggable,
  dragHandleRef,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const hoverNode = useSelector(state => state.viewStates.hoverNode);
  const getEditableExperiment = useEditableExperiment();
  const { user } = useSelector(state => state.auth);

  const [trophicLevelIsHovered, setTrophicLevelIsHovered] = useState(false);

  const isViewOnly = useMemo(() => {
    return !!matrix?.isViewOnly;
  }, [matrix]);

  const isPlayMode = useMemo(() => {
    return viewMode === 'play';
  }, [viewMode]);

  // Get an array of all the nodes
  let nodes = useMemo(() => [...Object.values(matrix.nodes)], [matrix]);

  // Responds to the new node button click
  const handleCreateNode = useCallback(
    trophicLevel => () => {
      if (!matrix?.parentId && user) {
        const node = createNode(trophicLevel);
        dispatch(addNode(matrix?.id, node));

        history.push(`/matrices/${matrix?.id}/overrides/${node.id}`);
      } else {
        const editableExperiment = getEditableExperiment(matrix);
        const { parentId, id } = editableExperiment;

        const node = createNode(trophicLevel);
        dispatch(addNode(id, node));

        history.push(
          `/matrices/${parentId}/experiments/${id}/overrides/${node.id}`
        );
      }
    },
    [dispatch, user, getEditableExperiment, history, matrix]
  );

  const nodesByTrophicLevel = useMemo(() => {
    const byTrophicLevel = nodes.reduce((acc, n) => {
      if (!acc[n?.trophicLevel]) {
        acc[n?.trophicLevel] = 0;
      }
      acc[n?.trophicLevel] = acc[n?.trophicLevel] + 1;
      return acc;
    }, {});
    return byTrophicLevel;
  }, [nodes]);

  const nodeScaling = useMemo(() => {
    const longestRow = Math.max(...Object.values(nodesByTrophicLevel), 1);
    let scale = Math.max(0, 6 - longestRow);
    scale = scale * 0.1;
    scale = 1 + scale;
    return scale;
  }, [nodesByTrophicLevel]);

  return (
    <div className="TrophicLevel">
      <div className="TrophicLevelMeta">
        <h4 className="TrophicLevelLabel">
          {icon ? (
            <img className="TrophicLevelLabelIcon" src={icon} alt={level} />
          ) : (
            <img
              className="TrophicLevelLabelIcon"
              src={icons?.trophicDefault}
              alt={level}
            />
          )}
          {level}
        </h4>
        {text.trophicLevels[level] ? (
          <Tooltip>{text.trophicLevels[level]}</Tooltip>
        ) : null}
        {!isViewOnly && !pseudoLevel ? (
          <ButtonNew
            buttonClasses="new-node-button"
            onClick={handleCreateNode(level)}
            label="New Node"
            iconLeft="plus"
          />
        ) : null}
      </div>

      {!!pseudoLevel && !currentNodes.length ? (
        <div className="TrophicLevelNodes">
          <button
            className="TrophicNodePlay-container TrophicLevel-new-node-button"
            style={{
              width: `${nodeScaling * 100}px`,
              height: `${nodeScaling * 100}px`,
            }}
            onClick={handleCreateNode(level)}
          >
            <div
              className="TrophicLevel-new-node-button-icon-container"
              style={{
                width: `${nodeScaling * 100 * 0.35}px`,
                height: `${nodeScaling * 100 * 0.35}px`,
              }}
            >
              <Icon icon="plus" className="TrophicLevel-new-node-button-icon" />
              <div className="TrophicLevel-new-node-button-text">New Node</div>
            </div>
          </button>
        </div>
      ) : null}

      {currentNodes.filter(node => node.trophicLevel === level)?.length ? (
        <div
          className="TrophicLevelNodes"
          onMouseMove={() => {
            if (hoverNode) dispatch(setNodeHover());
          }}
        >
          {currentNodes
            .filter(node => node.trophicLevel === level)
            .map(node => (
              <TrophicNodePlay
                key={`${node.id}=${matrix.id}`}
                numNodesAtTrophicLevel={
                  currentNodes.filter(node => node.trophicLevel === level)
                    ?.length ?? 0
                }
                node={node}
                matrix={matrix}
                // Actual node overrides
                nodeOverrides={nodeOverrides}
                trophicCascadeLevel={trophicCascadeLevel}
                trophicCascadeLevels={trophicCascadeLevels}
                // Pending node overrides
                addNodeOverride={n =>
                  setNodesToOverride([
                    ...nodesToOverride.filter(node => node.id !== n.id),
                    n,
                  ])
                }
                // Remove pending node override
                removeNodeOverride={n =>
                  setNodesToOverride([
                    ...nodesToOverride.filter(node => node.id !== n.id),
                  ])
                }
                // Pending node overrides
                fixed={isNodeFixed(node, nodes, nodesToOverride)}
                // Not yet overridden
                tempOverride={nodesToOverride.find(n => n.id === node.id)}
                trophicLevelInfo={{ name: level, color, icon }}
                nodeScaling={nodeScaling}
                canEdit={!isPlayMode}
              />
            ))}
          {draggable ? (
            <div className="TrophicLevelNodes-top-right" ref={dragHandleRef}>
              <Icon
                className={classNames('TrophicLevelNodes-icon', {
                  draggable,
                })}
                icon="dragHandle"
              />
            </div>
          ) : null}
        </div>
      ) : !pseudoLevel ? (
        <div
          className={classNames('TrophicLevelNodes deletable', {
            canDelete: trophicLevelIsHovered,
          })}
          onMouseEnter={() => setTrophicLevelIsHovered(true)}
          onMouseLeave={() => setTrophicLevelIsHovered(false)}
        >
          {canDelete && trophicLevelIsHovered ? (
            <ButtonNew
              buttonClasses="icon-button-small TrophicLevelCard-delete-button"
              icon={'trash'}
              onClick={() => onDelete(level)}
            />
          ) : null}
          {draggable ? (
            <div className="TrophicLevelNodes-top-right" ref={dragHandleRef}>
              <Icon
                className={classNames('TrophicLevelNodes-icon', {
                  draggable,
                })}
                icon="dragHandle"
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default TrophicLevel;
